import type { CodeProjectMeta, ProjectBreedKey } from '@ocode/domain';
import type { ProjectFile, ProjectKey } from '@repo/project-interface';
import sanitizeFilename from 'sanitize-filename';
import { deserializeProject, serializeProject } from '../serializer';
import { sourceToZip } from './sources-to-zip';
import { zipToSource } from './zip-to-source';

type ProjectData = ProjectFile[];

/**
 * ProjectSerializer의 기본 구현체 - js, p5, py 프로젝트에서 사용한다.
 *
 * Serializer의 입력은
 * 헤더와 작업 파일들이다.
 * 헤더는 projectKey와 projectName을 포함하고
 * 작업파일은 소스코드, 이미지 등을 사용자가 작성한 코드나 리소스 파일들을 의미한다.
 *
 * DefaultProjectSerializer는 src 폴더에 모든 작업 파일을 포함하는 형태이다.
 * 블록코딩은 폴더의 구조가 좀더 복잡하므로, 직접 ProjectSerializer를 구현해야 한다.
 */
export class DefaultProjectSerializer {
  /**
   * 소스코드 부분을 ZIP 파일로 만듭니다.
   * 서버에 업로드 할 때 Zip 파일 부분만 업로드 하는데, 이때 호출합니다.
   * 또는 download 할때 호출할 수 있습니다.
   */
  static createDataFile = async (filename: string, sourceFiles: File[]): Promise<File> => {
    const blob = await sourceToZip(sourceFiles);
    return new File([blob], sanitizeFilename(filename), { type: 'application/zip' });
  };

  static serializePjtFile = async (
    header: {
      breed: ProjectBreedKey;
      projectName: string;
      projectKey: ProjectKey | undefined;
    },
    sourceFiles: File[],
  ): Promise<File> => {
    const { breed, projectKey, projectName } = header;
    return await serializeProject(
      {
        breed,
        projectKey,
        projectName,
      },
      () => sourceToZip(sourceFiles),
    );
  };

  static deserializePjtFile = async (
    pjtFile: Blob,
  ): Promise<{
    meta: CodeProjectMeta;
    data: ProjectData;
  }> => {
    console.log('DefaultProjectSerializer.deserializePjtFile', pjtFile.size, 'bytes');
    return await deserializeProject(pjtFile, zipToSource);
  };
}
